import { Component } from '@theme/component';

/**
 * Vehicle Search Component
 * 
 * Handles the Make and Model dropdowns, populates models based on selected make,
 * and redirects to collection page with model filter.
 * 
 * @extends {Component}
 */
class VehicleSearchComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#parseData();
    this.#initializeRefs();
    this.#initializeModelOptions();
  }

  /**
   * Initializes refs after component is connected
   */
  #initializeRefs() {
    this.form = this.querySelector('form.vehicle-search__form');
    this.makeSelect = this.querySelector('select[name="make"]');
    this.modelSelect = this.querySelector('select[name="model"]');
    
    // Bind event handlers
    if (this.makeSelect) {
      this.makeSelect.addEventListener('change', this.handleMakeChange);
    }
    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit);
    }
  }

  /**
   * Parses the makes and models data from data attributes
   */
  #parseData() {
    const makesData = this.dataset.makes || '';
    const modelsData = this.dataset.models || '';

    // Parse makes: "handle:title||handle:title"
    this.makes = {};
    if (makesData) {
      const makesArray = makesData.split('||');
      for (const makeItem of makesArray) {
        const [handle, title] = makeItem.split(':');
        if (handle && title) {
          this.makes[handle] = title;
        }
      }
    }

    // Parse models: "handle::model1|model2|||handle::model1|model2"
    this.modelsByMake = {};
    if (modelsData) {
      const makesArray = modelsData.split('|||');
      for (const makeItem of makesArray) {
        const [handle, modelsString] = makeItem.split('::');
        if (handle && modelsString) {
          const models = modelsString.split('|').filter(m => m.trim() !== '');
          this.modelsByMake[handle] = models;
        }
      }
    }
  }

  /**
   * Initializes model options based on current make selection
   */
  #initializeModelOptions() {
    if (!this.makeSelect) return;
    const selectedMake = this.makeSelect.value;
    if (selectedMake) {
      this.#populateModels(selectedMake);
    }
  }

  /**
   * Handles make dropdown change event
   */
  handleMakeChange = () => {
    if (!this.makeSelect || !this.modelSelect) return;
    const selectedMake = this.makeSelect.value;
    
    // Clear and disable model dropdown
    this.modelSelect.innerHTML = '<option value="">Select Model (Optional)</option>';
    this.modelSelect.disabled = true;
    
    if (selectedMake) {
      this.#populateModels(selectedMake);
      this.modelSelect.disabled = false;
    }
  };

  /**
   * Populates model options for the selected make
   * @param {string} makeHandle - The handle of the selected make
   */
  #populateModels(makeHandle) {
    if (!this.modelSelect) return;
    const models = this.modelsByMake[makeHandle] || [];
    
    // Clear existing options except the first one
    this.modelSelect.innerHTML = '<option value="">Select Model (Optional)</option>';
    
    // Add model options
    for (const model of models) {
      const option = document.createElement('option');
      option.value = model;
      option.textContent = model;
      this.modelSelect.appendChild(option);
    }
  }

  /**
   * Handles form submission
   * @param {Event} event - The submit event
   */
  handleSubmit = (event) => {
    event.preventDefault();
    
    if (!this.makeSelect || !this.modelSelect) return;
    
    const makeHandle = this.makeSelect.value;
    const modelValue = this.modelSelect.value;
    
    if (!makeHandle) {
      return;
    }
    
    // Build collection URL
    const collectionUrl = `/collections/${makeHandle}`;
    const url = new URL(collectionUrl, window.location.origin);
    
    // Add model parameter if selected
    if (modelValue) {
      url.searchParams.set('model', modelValue);
    }
    
    // Redirect to collection page
    window.location.href = url.toString();
  };
}

if (!customElements.get('vehicle-search-component')) {
  customElements.define('vehicle-search-component', VehicleSearchComponent);
}

// Also handle form submission for forms without the component wrapper
document.addEventListener('DOMContentLoaded', () => {
  const forms = document.querySelectorAll('.vehicle-search__form');
  
  for (const form of forms) {
    if (!form.closest('vehicle-search-component')) {
      // Create a temporary component instance to handle this form
      const component = document.createElement('vehicle-search-component');
      component.appendChild(form.cloneNode(true));
      form.replaceWith(component);
    }
  }
});
